import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { buildAuthHeaders } from "../../utils/authSession";

function authHeaders() {
  return buildAuthHeaders();
}

function SummaryCards({ ad }) {
  const housingLabel = ad.wohnungsunterstuetzung_art === "unterkunft_wird_gestellt"
    ? "Unterkunft wird gestellt"
    : "Hilfe bei der Unterkunftssuche";
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <div className="dashboard-surface">
        <span className="dashboard-summary-label">Status</span>
        <strong>{ad.status || "-"}</strong>
        <p>Lifecycle bleibt unveraendert, die Vorschau dient nur der ruhigen Leseflaeche.</p>
      </div>
      <div className="dashboard-surface">
        <span className="dashboard-summary-label">Beruf</span>
        <strong>{ad.beruf_text || "-"}</strong>
        <p>Angezeigt wird der gespeicherte Snapshot, nicht eine neue Ableitung.</p>
      </div>
      <div className="dashboard-surface">
        <span className="dashboard-summary-label">Standort</span>
        <strong>{[ad.standort_land, ad.standort_ort].filter(Boolean).join(" / ") || "-"}</strong>
        <p>Die Vorschau bleibt Utility-Seite ohne Marketing-Hero oder schmale Mittelsaeule.</p>
      </div>
      <div className="dashboard-surface">
        <span className="dashboard-summary-label">Ankunftshilfe</span>
        <strong>{ad.gehalt_text || [ad.gehalt_von, ad.gehalt_bis].filter(Boolean).join(" - ") || "-"}</strong>
        <p>Willkommensbonus {ad.onboarding_bonus || 0} EUR · {housingLabel}</p>
      </div>
    </div>
  );
}

export default function StbPreview() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [error, setError] = useState("");

  const isDashboardPreview = location.pathname.startsWith("/dashboard/stb/preview/");

  useEffect(() => {
    document.title = "Experts4Europe | Anzeigenvorschau";
    axios
      .get(`${config.API_BASE_URL}/api/stb/employer/ads/${id}`, { headers: authHeaders() })
      .then((res) => setAd(res.data || null))
      .catch((e) => setError(e.response?.data?.message || "Vorschau konnte nicht geladen werden."));
  }, [id]);

  if (!isDashboardPreview) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100">
        <div className="mx-auto max-w-5xl rounded-[28px] border border-white/10 bg-slate-900/70 p-6 shadow-2xl">
          {error ? <p className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-100">{error}</p> : null}
          {!ad && !error ? <p className="text-sm text-slate-300">Lade Vorschau...</p> : null}
          {ad ? (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-cyan-300/35 bg-cyan-500/10 text-cyan-100">{ad.status}</Badge>
                <Badge variant="outline" className="border-white/20 bg-white/5 text-slate-100">{ad.arbeitsform}</Badge>
                <Badge variant="outline" className="border-emerald-300/35 bg-emerald-500/10 text-emerald-100">Willkommensbonus {ad.onboarding_bonus} EUR</Badge>
                <Badge variant="outline" className="border-emerald-300/35 bg-emerald-500/10 text-emerald-100">
                  {ad.wohnungsunterstuetzung_art === "unterkunft_wird_gestellt" ? "Unterkunft wird gestellt" : "Hilfe bei Unterkunftssuche"}
                </Badge>
              </div>
              <h1 className="text-3xl font-semibold text-white">{ad.titel}</h1>
              <p className="text-slate-300">{[ad.standort_land, ad.standort_ort].filter(Boolean).join(" / ") || "-"}</p>
              <div className="space-y-4 text-sm text-slate-200">
                <section className="rounded-2xl border border-white/10 bg-slate-900/50 p-4">
                  <h2 className="font-semibold text-white">Beschreibung</h2>
                  <p className="mt-2 whitespace-pre-wrap">{ad.beschreibung || "-"}</p>
                </section>
                <section className="rounded-2xl border border-white/10 bg-slate-900/50 p-4">
                  <h2 className="font-semibold text-white">Aufgaben</h2>
                  <p className="mt-2 whitespace-pre-wrap">{ad.aufgaben || "-"}</p>
                </section>
                <section className="rounded-2xl border border-white/10 bg-slate-900/50 p-4">
                  <h2 className="font-semibold text-white">Anforderungen</h2>
                  <p className="mt-2 whitespace-pre-wrap">{ad.anforderungen || "-"}</p>
                </section>
                <section className="rounded-2xl border border-white/10 bg-slate-900/50 p-4">
                  <h2 className="font-semibold text-white">Verbindliche Ankunftshilfe</h2>
                  <p className="mt-2 whitespace-pre-wrap">
                    {ad.wohnungsunterstuetzung_art === "unterkunft_wird_gestellt"
                      ? "Der Arbeitgeber stellt eine Unterkunft oder organisiert sie verbindlich."
                      : "Der Arbeitgeber hilft verbindlich bei der Suche einer geeigneten Unterkunft."}
                    {"\n"}Willkommensbonus: mindestens {ad.onboarding_bonus || 0} EUR.
                  </p>
                </section>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <EmployerPageFrame
      title="Anzeigenvorschau"
      subtitle="Ruhige Utility-Seite fuer Desktop und Mobile. Keine Marketing-Huelle, keine dunkle Fremdoptik innerhalb des Employer-Bereichs."
      actions={(
        <>
          <Button
            variant="outline"
            className="dashboard-action-button dashboard-action-button-secondary"
            onClick={() => navigate("/dashboard/employer/jobs")}
          >
            Zu Anzeigen
          </Button>
          <Button className="dashboard-action-button" onClick={() => navigate(`/dashboard/employer/jobs/wizard?id=${id}`)}>
            Bearbeiten
          </Button>
        </>
      )}
      contentClassName="space-y-4"
    >
      {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}
      {!ad && !error ? <p className="text-sm text-slate-500">Lade Vorschau...</p> : null}
      {ad ? (
        <>
          <section className="dashboard-card-shell space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">{ad.status}</Badge>
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{ad.arbeitsform || "-"}</Badge>
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Willkommensbonus {ad.onboarding_bonus || 0} EUR</Badge>
              <Badge variant="outline" className={Number(ad.wohnung) === 1 ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}>
                {ad.wohnungsunterstuetzung_art === "unterkunft_wird_gestellt" ? "Unterkunft wird gestellt" : "Hilfe bei Unterkunftssuche"}
              </Badge>
            </div>
            <div>
              <h2 className="dashboard-card-title">{ad.titel || "-"}</h2>
              <p className="dashboard-section-copy mt-2">Die Vorschau zeigt denselben Anzeigenstand wie die zugehoerige Dashboard-Aktion, ohne neue Inhaltsableitung.</p>
            </div>
            <SummaryCards ad={ad} />
          </section>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <section className="dashboard-card-shell space-y-4">
              <div>
                <span className="dashboard-kicker">Beschreibung</span>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{ad.beschreibung || "-"}</p>
              </div>
              <div>
                <span className="dashboard-kicker">Aufgaben</span>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{ad.aufgaben || "-"}</p>
              </div>
            </section>

            <section className="dashboard-card-shell space-y-4">
              <div>
                <span className="dashboard-kicker">Anforderungen</span>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{ad.anforderungen || "-"}</p>
              </div>
              <div>
                <span className="dashboard-kicker">Leistungen</span>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{ad.benefits || "-"}</p>
              </div>
              <div>
                <span className="dashboard-kicker">Verbindliche Ankunftshilfe</span>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                  {ad.wohnungsunterstuetzung_art === "unterkunft_wird_gestellt"
                    ? "Der Arbeitgeber stellt eine Unterkunft oder organisiert sie verbindlich."
                    : "Der Arbeitgeber hilft verbindlich bei der Suche einer geeigneten Unterkunft."}
                  {"\n"}Willkommensbonus: mindestens {ad.onboarding_bonus || 0} EUR.
                </p>
              </div>
            </section>
          </div>
        </>
      ) : null}
    </EmployerPageFrame>
  );
}
